import { useEffect, useRef, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { usePresentation } from '../../contexts/PresentationContext';
import useIsMobile from '../../hooks/useIsMobile';
import logoHorizontal from '../../assets/images/logo-horizontal.png';
import { PERFIL_OPCOES } from '../../utils/acreditacaoOpcoes';
import { iniciais } from '../../utils/nome';

const GRUPOS = [
  {
    label: 'Principal',
    itens: [{ path: '/dashboard', label: 'Dashboard', icon: 'dashboard', minPerfil: null }],
  },
  {
    label: 'Gestão',
    itens: [
      { path: '/hospitais', label: 'Hospitais', icon: 'local_hospital', minPerfil: 'operador' },
      { path: '/catalogo', label: 'Catálogo ONA', icon: 'checklist', minPerfil: 'operador' },
      { path: '/usuarios', label: 'Usuários', icon: 'group', minPerfil: 'operador', escondeEmApresentacao: true },
    ],
  },
  {
    label: 'Sistema',
    itens: [{ path: '/auditoria', label: 'Auditoria', icon: 'history', minPerfil: 'admin', escondeEmApresentacao: true }],
  },
];

// auditor_hospital fica entre operador e cliente: lança avaliação (como operador) mas só no
// próprio hospital e sem editar escopo/catálogo/usuários (como cliente) — os itens do menu que
// exigem minPerfil "operador" (Hospitais, Catálogo, Usuários) continuam escondidos pra ele.
const HIERARQUIA = { admin: 4, operador: 3, auditor_hospital: 2, cliente: 1 };
const CHAVE_COLAPSADA = 'am_sidebar_colapsada';

export default function Sidebar({ abertoMobile, onFecharMobile }) {
  const { user, logout } = useAuth();
  const { apresentacaoAtiva } = usePresentation();
  const isMobile = useIsMobile();
  const [colapsada, setColapsada] = useState(() => localStorage.getItem(CHAVE_COLAPSADA) === '1');
  const [menuUsuarioAberto, setMenuUsuarioAberto] = useState(false);
  const menuUsuarioRef = useRef(null);
  const colapsadaEfetiva = isMobile ? false : colapsada;
  const perfilLabel = PERFIL_OPCOES.find((p) => p.value === user?.perfil)?.label || user?.perfil;

  useEffect(() => {
    if (!menuUsuarioAberto) return;
    const fechar = (e) => {
      if (menuUsuarioRef.current?.contains(e.target)) return;
      setMenuUsuarioAberto(false);
    };
    const fecharComEsc = (e) => {
      if (e.key === 'Escape') setMenuUsuarioAberto(false);
    };
    document.addEventListener('mousedown', fechar);
    document.addEventListener('keydown', fecharComEsc);
    return () => {
      document.removeEventListener('mousedown', fechar);
      document.removeEventListener('keydown', fecharComEsc);
    };
  }, [menuUsuarioAberto]);

  const fecharMenuUsuarioENavegar = () => {
    setMenuUsuarioAberto(false);
    onFecharMobile();
  };

  const alternarColapso = () => {
    setColapsada((atual) => {
      const novo = !atual;
      localStorage.setItem(CHAVE_COLAPSADA, novo ? '1' : '0');
      return novo;
    });
  };

  return (
    <aside
      className={`app-sidebar${colapsadaEfetiva ? ' is-collapsed' : ''}${
        isMobile && abertoMobile ? ' is-mobile-open' : ''
      }`}
    >
      <div className="app-sidebar__brand">
        {colapsadaEfetiva ? (
          <div className="app-sidebar__mark">AM</div>
        ) : (
          <img src={logoHorizontal} alt="AM Gestão" />
        )}
      </div>

      <button
        type="button"
        className="app-sidebar__collapse-toggle"
        onClick={isMobile ? onFecharMobile : alternarColapso}
        title={isMobile ? 'Fechar menu' : colapsada ? 'Expandir menu' : 'Recolher menu'}
      >
        <span className="ms">{isMobile ? 'close' : colapsada ? 'chevron_right' : 'chevron_left'}</span>
      </button>

      <div className="app-sidebar__scroll">
        {GRUPOS.map((grupo) => {
          const itensVisiveis = grupo.itens.filter(
            (item) =>
              (!item.minPerfil || HIERARQUIA[user?.perfil] >= HIERARQUIA[item.minPerfil]) &&
              !(apresentacaoAtiva && item.escondeEmApresentacao)
          );
          if (itensVisiveis.length === 0) return null;
          return (
            <div key={grupo.label}>
              {!colapsadaEfetiva && <div className="app-sidebar__group-label">{grupo.label}</div>}
              <nav className="app-sidebar__nav">
                {itensVisiveis.map((item) => (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    title={colapsadaEfetiva ? item.label : undefined}
                    onClick={onFecharMobile}
                    className={({ isActive }) => `app-sidebar__link${isActive ? ' is-active' : ''}`}
                  >
                    <span className="ms">{item.icon}</span>
                    {!colapsadaEfetiva && <span>{item.label}</span>}
                  </NavLink>
                ))}
              </nav>
            </div>
          );
        })}
      </div>

      <div className="app-sidebar__footer" ref={menuUsuarioRef}>
        {menuUsuarioAberto && (
          <div className="app-sidebar__user-menu">
            <NavLink to="/novidades" className="app-sidebar__user-menu-item" onClick={fecharMenuUsuarioENavegar}>
              <span className="ms">celebration</span>
              Novidades
            </NavLink>
            <NavLink to="/perfil" className="app-sidebar__user-menu-item" onClick={fecharMenuUsuarioENavegar}>
              <span className="ms">person</span>
              Editar perfil
            </NavLink>
          </div>
        )}

        <button
          type="button"
          className="app-sidebar__user-btn"
          title="Novidades / Editar perfil"
          aria-haspopup="true"
          aria-expanded={menuUsuarioAberto}
          onClick={() => setMenuUsuarioAberto((v) => !v)}
        >
          <div className="app-sidebar__avatar">{iniciais(user?.nome)}</div>
          {!colapsadaEfetiva && (
            <div className="flex-grow-1" style={{ minWidth: 0, textAlign: 'left' }}>
              <div className="app-sidebar__user-name">{user?.nome}</div>
              <div className="app-sidebar__user-role">{perfilLabel}</div>
            </div>
          )}
        </button>
        {!colapsadaEfetiva && (
          <button className="app-sidebar__logout" onClick={logout} title="Sair" type="button">
            <span className="ms">logout</span>
          </button>
        )}
      </div>
    </aside>
  );
}
